import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Section, EmptyState, Pill } from "@/components/ui-kit";
import { Modal } from "./Books";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const empty = { name: "", type: "propio", location_ref: "", address: "", notes: "", active: true };
const TYPES = [
  { value: "propio", label: "Almacén propio" },
  { value: "distribuidor", label: "Depósito distribuidor" },
  { value: "deposito_cliente", label: "Depósito en cliente" },
];

export default function Settings() {
  const { user } = useAuth();
  const [whs, setWhs] = useState([]);
  const [dists, setDists] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  async function load() {
    const [w, d, c] = await Promise.all([api.get("/warehouses"), api.get("/distributors"), api.get("/customers")]);
    setWhs(w.data); setDists(d.data); setCustomers(c.data);
  }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      if (editing) await api.put(`/warehouses/${editing.id}`, form);
      else await api.post("/warehouses", form);
      toast.success("Almacén guardado");
      setShow(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  }
  async function del(id) {
    if (!window.confirm("¿Eliminar almacén? Los movimientos asociados se mantendrán pero no se verán referenciados.")) return;
    await api.delete(`/warehouses/${id}`); load();
  }

  function refName(w) {
    if (w.type === "distribuidor") return dists.find(d => d.id === w.location_ref)?.name || "—";
    if (w.type === "deposito_cliente") return customers.find(c => c.id === w.location_ref)?.name || "—";
    return "—";
  }

  const refOptions = form.type === "distribuidor" ? dists : form.type === "deposito_cliente" ? customers : [];

  return (
    <div data-testid="settings-page">
      <PageHeader title="Configuración" subtitle="Almacenes, usuarios y preferencias." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Section title="Usuario conectado" className="lg:col-span-1">
          <div className="p-5 space-y-2 text-sm">
            <div><span className="text-ink2 text-xs uppercase tracking-[0.1em]">Email</span><div className="font-mono">{user?.email}</div></div>
            <div><span className="text-ink2 text-xs uppercase tracking-[0.1em]">Rol</span><div><Pill tone="brand">{user?.role}</Pill></div></div>
            <div className="text-xs text-ink2 pt-3 border-t border-line">Para cambiar la contraseña, edita <code className="font-mono">ADMIN_PASSWORD</code> en el backend y reinicia. La app es de uso interno (2 usuarias).</div>
          </div>
        </Section>

        <Section title="Almacenes" className="lg:col-span-2" actions={
          <button onClick={() => { setEditing(null); setForm(empty); setShow(true); }}
            data-testid="new-warehouse-button"
            className="bg-brand hover:bg-brand-hover text-white text-sm px-3 py-1.5 rounded-sm inline-flex items-center gap-2">
            <Plus size={14} strokeWidth={1.5} /> Nuevo almacén
          </button>
        }>
          {whs.length === 0 ? <EmptyState title="Sin almacenes" description="Crea tu almacén propio primero." testid="warehouses-empty" /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-[11px] uppercase tracking-[0.1em] text-ink2 bg-sidebar/40">
                  <th className="text-left px-5 py-3 font-medium">Nombre</th>
                  <th className="text-left px-5 py-3 font-medium">Tipo</th>
                  <th className="text-left px-5 py-3 font-medium">Ref.</th>
                  <th className="text-right px-5 py-3 font-medium">Acciones</th>
                </tr></thead>
                <tbody>
                  {whs.map(w => (
                    <tr key={w.id} className="border-t border-line" data-testid={`warehouse-row-${w.id}`}>
                      <td className="px-5 py-3 font-medium">{w.name}</td>
                      <td className="px-5 py-3"><Pill tone={w.type === "propio" ? "brand" : w.type === "distribuidor" ? "success" : "warning"}>{w.type}</Pill></td>
                      <td className="px-5 py-3 text-ink2">{refName(w)}</td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => { setEditing(w); setForm({ ...empty, ...w }); setShow(true); }} className="text-xs text-ink2 hover:text-ink mr-3">Editar</button>
                        <button onClick={() => del(w.id)} className="text-xs text-crimson hover:opacity-80 inline-flex items-center gap-1"><Trash2 size={12} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>

      {show && (
        <Modal title={editing ? "Editar almacén" : "Nuevo almacén"} onClose={() => setShow(false)}>
          <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="warehouse-form">
            <F label="Nombre" required span={2}><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="warehouse-name" className={IN} /></F>
            <F label="Tipo" required>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, location_ref: "" })} data-testid="warehouse-type" className={IN}>
                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </F>
            {form.type !== "propio" && (
              <F label={form.type === "distribuidor" ? "Distribuidor" : "Cliente"}>
                <select value={form.location_ref} onChange={(e) => setForm({ ...form, location_ref: e.target.value })} className={IN}>
                  <option value="">—</option>
                  {refOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </F>
            )}
            <F label="Dirección" span={2}><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={IN} /></F>
            <F label="Notas" span={2}><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={IN} /></F>
            <div className="col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm border border-line rounded-sm hover:bg-sidebar">Cancelar</button>
              <button type="submit" data-testid="warehouse-submit" className="px-4 py-2 text-sm bg-brand hover:bg-brand-hover text-white rounded-sm">Guardar</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const IN = "w-full bg-white border border-line rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand";
function F({ label, children, required, span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "col-span-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-[0.12em] text-ink2">{label}{required && <span className="text-brand"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
